import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import "./Auth.css";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle } = useAuth();
  const { t, language } = useTranslation();
  // Google's button needs a width in pixels (max 400): follow the card width.
  const googleBoxRef = useRef(null);
  const [googleWidth, setGoogleWidth] = useState(320);
  useEffect(() => {
    const el = googleBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry.contentRect.width);
      if (w) setGoogleWidth(Math.max(200, Math.min(400, w)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const resolveRedirectPath = () => {
    const stateFrom = location.state?.from;
    const statePath =
      stateFrom && typeof stateFrom === "object"
        ? `${stateFrom.pathname || ""}${stateFrom.search || ""}`
        : "";

    const blocked = new Set(["/login", "/signup", "/pending-verification"]);
    if (statePath && !blocked.has(statePath)) {
      return statePath;
    }

    try {
      const lastRoute = sessionStorage.getItem("casaclass:lastRoute");
      if (lastRoute && !blocked.has(lastRoute)) {
        return lastRoute;
      }
    } catch (e) {
      // ignore
    }

    return "/dashboard";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setFormLoading(true);

    try {
      const nextUser = await login(form.email, form.password);
      if (nextUser?.role === "teacher" && !nextUser.is_verified) {
        navigate("/pending-verification", { replace: true });
      } else {
        navigate(resolveRedirectPath(), { replace: true });
      }
    } catch (err) {
      setError(err.message || t("authPage.loginError"));
    } finally {
      setFormLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      setError(t("authPage.googleError"));
      return;
    }

    setError("");
    setGoogleLoading(true);

    try {
      const nextUser = await loginWithGoogle(idToken);
      if (nextUser?.role === "teacher" && !nextUser.is_verified) {
        navigate("/pending-verification", { replace: true });
      } else {
        navigate(resolveRedirectPath(), { replace: true });
      }
    } catch (err) {
      setError(err.message || t("authPage.googleError"));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t("authPage.googleError"));
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <h1>{t("authPage.loginTitle")}</h1>
        <p>{t("authPage.loginLead")}</p>

        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="email">{t("authPage.email")}</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("authPage.emailPlaceholder")}
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">{t("authPage.password")}</label>
            <div className="password-wrap">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("authPage.passwordPlaceholder")}
                value={form.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
                title={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="auth-helper">
            <Link to="/forgot-password">{t("authPage.forgotLink")}</Link>
          </div>

          <div className="auth-actions">
            <button
              type="submit"
              className="btn primary"
              disabled={formLoading || googleLoading}
            >
              {formLoading
                ? t("authPage.loginLoading")
                : t("authPage.loginCta")}
            </button>
          </div>
        </form>

        <div className="auth-social">
          <div className="auth-divider">
            <span>{t("authPage.socialDivider")}</span>
          </div>
          <div className="auth-google" ref={googleBoxRef}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              width={String(googleWidth)}
              locale={language}
              theme="outline"
              shape="rectangular"
              text="signin_with"
            />
          </div>
          {googleLoading && (
            <p className="auth-progress">{t("authPage.googleLoading")}</p>
          )}
        </div>

        <div className="auth-alt">
          {t("authPage.noAccount")}{" "}
          <Link to="/signup">{t("authPage.goSignup")}</Link>
        </div>
      </div>
    </section>
  );
}
